import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import User from 'apollo-react-icons/User';
import ConversationChat from 'apollo-react-icons/ConversationChat';
import Avatar from 'apollo-react/components/Avatar';
import CircularProgress from 'apollo-react/components/CircularProgress';
import CommentToggle from '../Comments/CommentToggle';
import CommentViewer from '../Comments/CommentViewer';
import AssessmentStatusBadge from './AssessmentStatusBadge';
import useAssessmentReviewApi from '../../../hooks/api/assessmentReview/useAssessmentReviewApi';
import styles from './AssessmentReviewerSection.module.css';

const AssessmentReviewerSection = ({
  assignmentId,
  reviewerName,
  reviewerEmail,
  assessmentStatus,
  rejectionReason,
  isOwnSection,
  callerRole,
  sectionRef,
  currentUserEmail,
}) => {
  const { t } = useTranslation();
  const { getComments, addComment } = useAssessmentReviewApi();
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(true);

  const isHidden =
    callerRole === 'assessmentReviewer' &&
    !isOwnSection &&
    assessmentStatus === 'pending';

  useEffect(() => {
    if (isHidden) return;
    getComments(assignmentId)
      .then((res) => setComments(res.data))
      .finally(() => setCommentsLoading(false));
  }, [assignmentId, getComments, isHidden]);

  if (isHidden) return null;

  const canComment = isOwnSection || callerRole === 'mainReviewer';
  const progressActiveForViewer = { active: true };

  const refetchComments = () =>
    getComments(assignmentId)
      .then((res) => setComments(res.data))
      .catch(() => {});

  const handleAddComment = async ({ comment, attachedFiles }) => {
    await addComment(assignmentId, comment, attachedFiles);
    await refetchComments();
  };

  const handleCommentUpdated = async () => {
    await refetchComments();
  };

  const handleCommentDeleted = async () => {
    await refetchComments();
  };

  return (
    <div ref={sectionRef} className={styles.sectionCard}>
      <div className={styles.header}>
        <AssessmentStatusBadge status={assessmentStatus} />
      </div>
      <div className={styles.profileCard}>
        <Avatar className={styles.profileAvatar} stateless>
          <User />
        </Avatar>
        <div className={styles.profileInfo}>
          <span className={styles.profileName}>{reviewerName}</span>
          <span className={styles.profileEmail}>{reviewerEmail}</span>
        </div>
      </div>
      {assessmentStatus === 'rejected' && rejectionReason && (
        <div className={styles.rejectionReasonBox}>
          <span className={styles.rejectionReasonLabel}>
            {t('rejectionReason')}:{' '}
          </span>
          <span className={styles.rejectionReasonText}>{rejectionReason}</span>
        </div>
      )}
      <div className={styles.commentsSection}>
        {commentsLoading ? (
          <CircularProgress />
        ) : (
          <>
            {comments.length === 0 ? (
              <div className={styles.emptyState}>
                <ConversationChat />
                <span className={styles.emptyStateText}>
                  {t('noCommentsYet')}
                </span>
              </div>
            ) : (
              <CommentViewer
                comments={comments}
                hideHeader
                isAssessmentReviewerComment
                requestProgress={progressActiveForViewer}
                requestDateEnded={null}
                currentUserEmail={currentUserEmail}
                onCommentUpdated={handleCommentUpdated}
                onCommentDeleted={handleCommentDeleted}
              />
            )}
            {canComment && (
              <CommentToggle
                onAddComment={handleAddComment}
                hasComments={comments.length > 0}
                buttonText={
                  callerRole === 'mainReviewer' ? t('sendComment') : undefined
                }
                hideTitleSeparator
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AssessmentReviewerSection;
